import { Link } from "react-router-dom";
import { Scale, Car, Truck, Wrench, Stethoscope, ArrowRight, DollarSign, Handshake, TrendingUp, Briefcase } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";
import AIReadableSection from "@/components/AIReadableSection";
import { personalInjuryFAQs } from "@/lib/practice-area-faqs";
import HeroSection from "@/components/HeroSection";

import personalInjuryHero from "@/assets/personal-injury-hero.jpg";

const PersonalInjury = () => {
  const { language } = useLanguage();

  const copy = {
    title: {
      en: "Personal Injury",
      es: "Lesiones Personales",
      vi: "Tổn Thương Cá Nhân",
    },
    subtitle: {
      en: "Fighting for maximum compensation for injury victims. We hold negligent parties accountable and get you the settlement you deserve.",
      es: "Luchamos por la máxima compensación para las víctimas. Hacemos responsables a los negligentes y obtenemos el acuerdo que merece.",
      vi: "Đấu tranh để giành bồi thường tối đa cho nạn nhân. Chúng tôi buộc bên gây ra chịu trách nhiệm và đạt được khoản bồi thường xứng đáng.",
    },
    schedule: { en: "Schedule Consultation", es: "Agende Consulta", vi: "Đặt Lịch Tư Vấn" },
    call: { en: "Call:", es: "Llamar:", vi: "Gọi:" },
    noFee: {
      en: "No Win, No Fee - We Advance All Case Costs",
      es: "Sin Cobro si No Ganamos - Adelantamos Todos los Costos del Caso",
      vi: "Không Thắng, Không Phí - Chúng tôi ứng trước mọi chi phí vụ việc",
    },
    typesTitle: {
      en: "Types of Personal Injury Cases We Handle",
      es: "Tipos de Casos de Lesiones Personales que Atendemos",
      vi: "Các Loại Vụ Việc Tổn Thương Cá Nhân Chúng Tôi Xử Lý",
    },
    typesSubtitle: {
      en: "Experienced representation for all types of injury claims",
      es: "Representación con experiencia para todo tipo de reclamos por lesiones",
      vi: "Đại diện dày dạn kinh nghiệm cho mọi loại yêu cầu bồi thường",
    },
    learnMore: { en: "Learn More", es: "Ver Más", vi: "Tìm Hiểu" },
    compensationTitle: {
      en: "What Compensation Can You Receive?",
      es: "¿Qué Compensación Puede Recibir?",
      vi: "Bạn Có Thể Nhận Những Khoản Bồi Thường Nào?",
    },
    compensation: {
      medical: {
        title: { en: "Medical Expenses", es: "Gastos Médicos", vi: "Chi Phí Y Tế" },
        desc: {
          en: "All past and future medical bills, including surgeries, therapy, and medications",
          es: "Todas las facturas médicas pasadas y futuras, incluidas cirugías, terapias y medicamentos",
          vi: "Tất cả chi phí y tế quá khứ và tương lai, bao gồm phẫu thuật, trị liệu và thuốc men",
        },
      },
      wages: {
        title: { en: "Lost Wages", es: "Salarios Perdidos", vi: "Mất Thu Nhập" },
        desc: {
          en: "Income lost due to injury and reduced future earning capacity",
          es: "Ingresos perdidos debido a la lesión y disminución de la capacidad de ganancia futura",
          vi: "Thu nhập bị mất do chấn thương và giảm khả năng kiếm tiền trong tương lai",
        },
      },
      pain: {
        title: { en: "Pain & Suffering", es: "Dolor y Sufrimiento", vi: "Đau Đớn & Tổn Thất" },
        desc: {
          en: "Physical pain, emotional distress, and reduced quality of life",
          es: "Dolor físico, angustia emocional y disminución de la calidad de vida",
          vi: "Đau đớn thể xác, tổn thương tinh thần và giảm chất lượng cuộc sống",
        },
      },
      property: {
        title: { en: "Property Damage", es: "Daños a la Propiedad", vi: "Thiệt Hại Tài Sản" },
        desc: {
          en: "Vehicle repairs or replacement and other damaged property",
          es: "Reparación o reemplazo de vehículo y otros bienes dañados",
          vi: "Sửa chữa hoặc thay thế xe và tài sản bị hư hại khác",
        },
      },
      punitiveTitle: { en: "Punitive Damages", es: "Daños Punitivos", vi: "Bồi Thường Trừng Phạt" },
      punitiveDesc: {
        en: "In cases of gross negligence or intentional harm, you may also be entitled to punitive damages designed to punish the wrongdoer and deter similar conduct.",
        es: "En casos de negligencia grave o daño intencional, también puede tener derecho a daños punitivos diseñados para castigar al infractor y disuadir conductas similares.",
        vi: "Trong trường hợp cẩu thả nghiêm trọng hoặc cố ý gây hại, bạn cũng có thể được bồi thường trừng phạt nhằm trừng phạt người gây hại và ngăn chặn hành vi tương tự.",
      },
    },
    processTitle: {
      en: "Our Personal Injury Process",
      es: "Nuestro Proceso en Lesiones Personales",
      vi: "Quy Trình Xử Lý Tổn Thương Cá Nhân",
    },
    process: [
      {
        title: { en: "Consultation & Investigation", es: "Consulta e Investigación", vi: "Tư Vấn & Điều Tra" },
        desc: {
          en: "We review your case at no cost. If we take your case, we immediately begin investigating: gathering evidence, interviewing witnesses, and consulting medical experts.",
          es: "Revisamos su caso sin costo. Si lo tomamos, comenzamos de inmediato la investigación: recopilación de evidencia, entrevistas a testigos y consulta con expertos médicos.",
          vi: "Chúng tôi xem xét vụ việc miễn phí. Nếu nhận vụ, chúng tôi điều tra ngay: thu thập chứng cứ, phỏng vấn nhân chứng và tham vấn chuyên gia y tế.",
        },
      },
      {
        title: { en: "Medical Treatment & Documentation", es: "Tratamiento Médico y Documentación", vi: "Điều Trị & Hồ Sơ Y Tế" },
        desc: {
          en: "We help you find appropriate medical care and ensure all injuries are documented. Proper medical records are crucial for maximum compensation.",
          es: "Le ayudamos a encontrar atención médica adecuada y aseguramos que todas las lesiones estén documentadas. Los registros médicos adecuados son clave para la máxima compensación.",
          vi: "Chúng tôi hỗ trợ tìm chăm sóc y tế phù hợp và đảm bảo chấn thương được ghi nhận. Hồ sơ y tế đầy đủ là yếu tố then chốt.",
        },
      },
      {
        title: { en: "Demand & Negotiation", es: "Demanda y Negociación", vi: "Yêu Cầu & Đàm Phán" },
        desc: {
          en: "We prepare a comprehensive demand package and negotiate aggressively with insurance companies for fair settlement.",
          es: "Preparamos un paquete de demanda completo y negociamos agresivamente con las aseguradoras para un acuerdo justo.",
          vi: "Chúng tôi chuẩn bị hồ sơ yêu cầu toàn diện và đàm phán quyết liệt với bảo hiểm để đạt thỏa thuận công bằng.",
        },
      },
      {
        title: { en: "Litigation if Necessary", es: "Litigio si es Necesario", vi: "Khởi Kiện Khi Cần" },
        desc: {
          en: "If insurers won't offer fair compensation, we file a lawsuit and take your case to trial. We're experienced trial attorneys ready to fight for you in court.",
          es: "Si las aseguradoras no ofrecen una compensación justa, presentamos una demanda y llevamos su caso a juicio. Somos abogados litigantes con experiencia.",
          vi: "Nếu bảo hiểm không đưa đề nghị công bằng, chúng tôi khởi kiện và đưa vụ việc ra tòa. Chúng tôi giàu kinh nghiệm tranh tụng.",
        },
      },
    ],
    faqTitle: { en: "Frequently Asked Questions", es: "Preguntas Frecuentes", vi: "Câu Hỏi Thường Gặp" },
    faqs: [
      {
        q: {
          en: "How much is my personal injury case worth?",
          es: "¿Cuánto vale mi caso de lesiones personales?",
          vi: "Giá trị vụ việc của tôi là bao nhiêu?",
        },
        a: {
          en: "Case value depends on multiple factors: severity of injuries, medical expenses, lost wages, permanent disability, pain and suffering, and liability. Minor soft tissue injuries might settle for thousands, while catastrophic injuries can be worth millions. We provide case evaluations to assess your claim's potential value.",
          es: "El valor depende de varios factores: gravedad de las lesiones, gastos médicos, salarios perdidos, discapacidad permanente, dolor y sufrimiento, y responsabilidad. Lesiones menores pueden resolverse por miles; las catastróficas por millones. Evaluamos su caso para estimar el valor.",
          vi: "Giá trị phụ thuộc vào nhiều yếu tố: mức độ chấn thương, chi phí y tế, thu nhập mất, tàn tật vĩnh viễn, đau đớn và trách nhiệm. Vết thương nhẹ có thể vài nghìn; thảm khốc có thể hàng triệu. Chúng tôi đánh giá vụ việc để ước tính giá trị.",
        },
      },
      {
        q: {
          en: "How long do I have to file a personal injury claim in Texas?",
          es: "¿Cuánto tiempo tengo para presentar un reclamo en Texas?",
          vi: "Tôi có bao lâu để nộp yêu cầu ở Texas?",
        },
        a: {
          en: "Texas has a 2-year statute of limitations for most personal injury cases from the date of injury. However, some cases have shorter deadlines, and evidence disappears quickly. Don't wait - contact us immediately after your injury to preserve your claim and evidence.",
          es: "Texas tiene un plazo de 2 años para la mayoría de los casos desde la fecha de la lesión. Algunos casos tienen plazos más cortos y la evidencia desaparece rápido. No espere: contáctenos de inmediato para preservar su reclamo.",
          vi: "Texas có thời hiệu 2 năm cho hầu hết vụ việc kể từ ngày bị thương. Một số có thời hạn ngắn hơn và bằng chứng mất nhanh. Đừng chần chờ—hãy liên hệ ngay để bảo vệ yêu cầu.",
        },
      },
      {
        q: { en: "What if I was partially at fault for the accident?", es: "¿Y si tuve parte de la culpa?", vi: "Nếu tôi cũng có lỗi thì sao?" },
        a: {
          en: "Texas follows 'modified comparative negligence' - you can still recover if you're less than 51% at fault. Your compensation is reduced by your percentage of fault. For example, if you're 20% at fault for a $100,000 claim, you'd receive $80,000. We fight to minimize your fault percentage and maximize your recovery.",
          es: "Texas aplica la 'negligencia comparativa modificada': puede recuperar si su culpa es menor al 51%. Su compensación se reduce según su porcentaje de culpa. Luchamos por minimizar su porcentaje y maximizar su recuperación.",
          vi: "Texas áp dụng 'sơ suất so sánh điều chỉnh'—bạn vẫn có thể được bồi thường nếu lỗi dưới 51%. Khoản bồi thường giảm theo tỷ lệ lỗi. Chúng tôi nỗ lực giảm tỷ lệ lỗi và tối đa hóa bồi thường.",
        },
      },
      {
        q: { en: "Should I accept the insurance company's settlement offer?", es: "¿Debo aceptar la oferta de la aseguradora?", vi: "Có nên chấp nhận đề nghị của bảo hiểm?" },
        a: {
          en: "NO - not without consulting an attorney first. Insurance companies routinely make lowball initial offers hoping you'll settle quickly. Once you accept, you can't get more money later. We review all offers and negotiate for fair compensation that covers all your damages, not just immediate medical bills.",
          es: "NO, sin consultar primero con un abogado. Las aseguradoras suelen ofrecer montos bajos al inicio. Una vez que acepta, no podrá obtener más. Revisamos y negociamos para lograr una compensación justa que cubra todos sus daños.",
          vi: "KHÔNG—hãy tham vấn luật sư trước. Bảo hiểm thường đề nghị thấp lúc đầu. Khi đã chấp nhận, bạn không thể nhận thêm. Chúng tôi xem xét và đàm phán để có khoản bồi thường công bằng.",
        },
      },
      {
        q: { en: "How much does a personal injury lawyer cost?", es: "¿Cuánto cuesta un abogado de lesiones personales?", vi: "Thuê luật sư tổn thương cá nhân tốn bao nhiêu?" },
        a: {
          en: "We work on contingency - no fee unless we win your case. We advance all case expenses and only get paid when you receive compensation.",
          es: "Trabajamos por contingencia: no cobramos a menos que ganemos su caso. Adelantamos los gastos y solo cobramos si usted recibe compensación.",
          vi: "Chúng tôi làm việc theo tỷ lệ phần trăm: không thu phí nếu không thắng. Ứng trước chi phí và chỉ nhận khi bạn được bồi thường.",
        },
      },
    ],
    whyTitle: {
      en: "Why Choose Us for Your Injury Claim?",
      es: "¿Por Qué Elegirnos para su Reclamo de Lesiones?",
      vi: "Vì Sao Chọn Chúng Tôi Cho Yêu Cầu Bồi Thường Của Bạn?",
    },
    why: [
      {
        title: { en: "Aggressive Negotiation", es: "Negociación Agresiva", vi: "Đàm Phán Quyết Liệt" },
        desc: {
          en: "We fight insurance companies hard to get you maximum compensation - not quick, low settlements.",
          es: "Luchamos con las aseguradoras para obtener la máxima compensación, no acuerdos bajos y rápidos.",
          vi: "Chúng tôi đấu tranh với bảo hiểm để đạt bồi thường tối đa, không chấp nhận đề nghị thấp.",
        },
      },
      {
        title: { en: "Proven Results", es: "Resultados Probados", vi: "Kết Quả Được Chứng Minh" },
        desc: {
          en: "Track record of substantial settlements and verdicts for our injury clients.",
          es: "Historial de acuerdos y veredictos significativos para nuestros clientes.",
          vi: "Thành tích đạt nhiều khoản bồi thường lớn cho khách hàng.",
        },
      },
      {
        title: { en: "No Upfront Costs", es: "Sin Costos Iniciales", vi: "Không Chi Phí Ban Đầu" },
        desc: {
          en: "We advance all expenses and only get paid when you win. Zero risk to you.",
          es: "Adelantamos todos los gastos y solo cobramos cuando gana. Cero riesgo para usted.",
          vi: "Chúng tôi ứng trước mọi chi phí và chỉ nhận phí khi bạn thắng. Không rủi ro cho bạn.",
        },
      },
    ],
    ctaTitle: {
      en: "Injured? Get the Compensation You Deserve",
      es: "¿Lesionado? Obtenga la Compensación que Merece",
      vi: "Bị Thương? Hãy Nhận Khoản Bồi Thường Xứng Đáng",
    },
    ctaDesc: {
      en: "Don't let insurance companies take advantage of you. Contact us for a case review and fight for what's rightfully yours.",
      es: "No permita que las aseguradoras se aprovechen de usted. Contáctenos para una revisión de su caso y luche por lo que le corresponde.",
      vi: "Đừng để công ty bảo hiểm lợi dụng bạn. Liên hệ với chúng tôi để đánh giá vụ việc và bảo vệ quyền lợi của bạn.",
    },
  };

  const servicesData = [
    {
      icon: Car,
      title: { en: "Auto Accidents", es: "Accidentes de Auto", vi: "Tai Nạn Ô Tô" },
      description: {
        en: "Maximum compensation for car accident injuries, property damage, and lost wages. We handle all insurance negotiations.",
        es: "Compensación máxima por lesiones de auto, daños a la propiedad y salarios perdidos. Manejamos toda la negociación con aseguradoras.",
        vi: "Bồi thường tối đa cho chấn thương, thiệt hại tài sản và mất thu nhập do tai nạn xe. Chúng tôi xử lý đàm phán với bảo hiểm.",
      },
      href: "/practice-areas/personal-injury/auto-accident",
    },
    {
      icon: Truck,
      title: { en: "Truck Accidents", es: "Accidentes de Camiones", vi: "Tai Nạn Xe Tải" },
      description: {
        en: "Complex litigation against trucking companies and their insurers for serious truck accident injuries.",
        es: "Litigios complejos contra compañías de camiones y aseguradoras por lesiones graves.",
        vi: "Kiện tụng phức tạp chống lại công ty xe tải và bảo hiểm khi bị thương nghiêm trọng.",
      },
      href: "/practice-areas/personal-injury/truck-accident",
    },
    {
      icon: Wrench,
      title: { en: "Industrial Accidents", es: "Accidentes Industriales", vi: "Tai Nạn Công Nghiệp" },
      description: {
        en: "Workplace injury claims including construction accidents, refinery injuries, and industrial equipment accidents.",
        es: "Reclamos por lesiones laborales: construcción, refinerías y equipos industriales.",
        vi: "Các yêu cầu chấn thương nơi làm việc: xây dựng, nhà máy lọc dầu và thiết bị công nghiệp.",
      },
      href: "/practice-areas/personal-injury/industrial-accident",
    },
    {
      icon: Stethoscope,
      title: { en: "Medical Malpractice", es: "Negligencia Médica", vi: "Sơ Suất Y Khoa" },
      description: {
        en: "Holding healthcare providers accountable for medical negligence, surgical errors, and misdiagnosis.",
        es: "Responsabilizamos a proveedores de salud por negligencia médica, errores quirúrgicos y diagnósticos incorrectos.",
        vi: "Buộc cơ sở y tế chịu trách nhiệm về sơ suất, sai sót phẫu thuật và chẩn đoán sai.",
      },
      href: "/practice-areas/personal-injury/medical-malpractice",
    },
  ];

  const services = servicesData.map((s) => ({
    icon: s.icon,
    title: s.title[language],
    description: s.description[language],
    href: s.href,
  }));

  const compensationTypes = [
    { title: copy.compensation.medical.title[language], description: copy.compensation.medical.desc[language] },
    { title: copy.compensation.wages.title[language], description: copy.compensation.wages.desc[language] },
    { title: copy.compensation.pain.title[language], description: copy.compensation.pain.desc[language] },
    { title: copy.compensation.property.title[language], description: copy.compensation.property.desc[language] },
  ];

  const faqs = copy.faqs.map((f) => ({ question: f.q[language], answer: f.a[language] }));

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section - Optimized for CWV */}
      <HeroSection
        imageSrc={personalInjuryHero}
        imageAlt="Personal Injury Attorney Houston - Auto Accidents, Medical Malpractice"
        height="600px"
        overlayGradient="bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"
        priority={true}
      >
        <div className="max-w-4xl mx-auto text-center text-primary-foreground">
          <Scale className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">{copy.title[language]}</h1>
          <p className="text-xl opacity-95 mb-8 drop-shadow-md">{copy.subtitle[language]}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero">
              <LocalizedLink to="/booking">{copy.schedule[language]}</LocalizedLink>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary-dark bg-white/10 backdrop-blur-sm">
                {copy.call[language]} (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </HeroSection>

      {/* No Fee Banner */}
      <section className="py-6 bg-accent text-accent-foreground">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg font-bold flex items-center justify-center gap-2">
            <DollarSign className="h-6 w-6" />
            {copy.noFee[language]}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{copy.typesTitle[language]}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {copy.typesSubtitle[language]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-accent" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="link" className="p-0">
                    <LocalizedLink to={service.href}>
                      {copy.learnMore[language]} <ArrowRight className="ml-1 h-4 w-4" />
                    </LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compensation Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.compensationTitle[language]}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {compensationTypes.map((type, index) => (
                <div key={index} className="bg-card p-6 rounded-lg shadow-md">
                  <h3 className="text-xl font-bold mb-3">{type.title}</h3>
                  <p className="text-muted-foreground">{type.description}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <h3 className="text-lg font-bold mb-2">{copy.compensation.punitiveTitle[language]}</h3>
              <p className="text-muted-foreground">{copy.compensation.punitiveDesc[language]}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.processTitle[language]}</h2>
            <div className="space-y-6">
              {copy.process.map((step, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">
                    {idx + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{step.title[language]}</h3>
                    <p className="text-muted-foreground">{step.desc[language]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.faqTitle[language]}</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{copy.whyTitle[language]}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {copy.why.map((w, i) => {
                const icons = [Handshake, TrendingUp, Briefcase];
                const IconComponent = icons[i];
                return (
                  <div key={i}>
                    <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{w.title[language]}</h3>
                    <p className="text-sm text-muted-foreground">{w.desc[language]}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AI Readable Section for LLMs and AI Search */}
      <AIReadableSection
        title={personalInjuryFAQs[language].title}
        summary={personalInjuryFAQs[language].summary}
      />

      <RelatedLinks category="personalInjury" />

      <CTASection
        title={copy.ctaTitle[language]}
        description={copy.ctaDesc[language]}
      />

      <Footer />
    </div>
  );
};

export default PersonalInjury;
